(() => {
  const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const toast = (message) => {
    const el = document.querySelector('[data-bazaar-toast]');
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    window.clearTimeout(el._timer);
    el._timer = window.setTimeout(() => { el.hidden = true; }, 2200);
  };

  // Phase 4.1 immersive entrance: camera push, door reveal, and travel through into the grand hall.
  const intro = document.querySelector('[data-bazaar-intro]');
  if (intro) {
    const storageKey = 'hiddenBazaarIntroSeenV41';
    const repeat = intro.dataset.repeat === 'true';
    const editor = window.Shopify && Shopify.designMode;
    const duration = Math.max(0, Number(intro.dataset.doorDuration || 1600));
    const approachDuration = Math.max(0, Number(intro.dataset.approachDuration || 950));
    const travelDuration = Math.max(0, Number(intro.dataset.travelDuration || 1200));
    intro.style.setProperty('--door-duration', `${duration}ms`);
    intro.style.setProperty('--approach-duration', `${approachDuration}ms`);
    intro.style.setProperty('--travel-duration', `${travelDuration}ms`);
    let seen = false;
    try { seen = sessionStorage.getItem(storageKey) === '1'; } catch (_) {}

    if (!repeat && !editor && seen) {
      intro.remove();
      document.body.classList.remove('no-scroll');
    } else {
      document.body.classList.add('no-scroll');
      const enter = intro.querySelector('[data-enter-bazaar]');
      let leaving = false;
      if (enter) window.setTimeout(() => enter.focus({ preventScroll: true }), 80);

      const leave = (animate = true) => {
        if (leaving) return;
        leaving = true;
        try { if (!repeat && !editor) sessionStorage.setItem(storageKey, '1'); } catch (_) {}
        const shouldAnimate = animate && !reducedMotion;
        if (!shouldAnimate) {
          intro.classList.add('is-leaving');
          document.body.classList.remove('no-scroll');
          window.setTimeout(() => intro.remove(), 0);
          return;
        }

        intro.classList.add('is-approaching');
        window.setTimeout(() => {
          intro.classList.add('is-opening');
          window.setTimeout(() => {
            intro.classList.add('is-traveling');
            window.setTimeout(() => {
              intro.classList.add('is-leaving');
              document.body.classList.remove('no-scroll');
              window.setTimeout(() => intro.remove(), 750);
            }, travelDuration + 80);
          }, Math.max(260, Math.round(duration * 0.52)));
        }, approachDuration);
      };

      intro.querySelectorAll('[data-enter-bazaar]').forEach(btn => btn.addEventListener('click', () => leave(true)));
      intro.querySelectorAll('[data-skip-bazaar]').forEach(btn => btn.addEventListener('click', () => leave(false)));
      intro.addEventListener('keydown', e => {
        if (e.key === 'Escape') leave(false);
        if (e.key === 'Tab') {
          const focusable = [...intro.querySelectorAll('button:not([disabled]),a[href]')];
          if (!focusable.length) return;
          const first = focusable[0], last = focusable[focusable.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      });
    }
  }

  // Collection sorting.
  const sort = document.querySelector('[data-sort-select]');
  if (sort) {
    sort.addEventListener('change', () => {
      const url = new URL(window.location.href);
      url.searchParams.set('sort_by', sort.value);
      window.location.assign(url.toString());
    });
  }

  // Variant state.
  document.querySelectorAll('[data-product-root]').forEach(root => {
    const select = root.querySelector('[data-variant-select]');
    if (!select) return;
    const price = root.querySelector('[data-product-price]');
    const compare = root.querySelector('[data-compare-price]');
    const add = root.querySelector('[data-add-button]');
    const label = root.querySelector('[data-add-label]');
    const sync = () => {
      const option = select.options[select.selectedIndex];
      if (!option) return;
      if (price) price.textContent = option.dataset.price || '';
      if (compare) {
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
      }
      const available = option.dataset.available === 'true';
      if (add) add.disabled = !available;
      if (label) label.textContent = available ? 'Add to cart' : 'Sold out';
    };
    select.addEventListener('change', sync);
    sync();
  });

  // Bazaar compass.
  document.querySelectorAll('[data-bazaar-compass]').forEach(compass => {
    const trigger = compass.querySelector('[data-compass-trigger]');
    const panel = compass.querySelector('[data-compass-panel]');
    const close = compass.querySelector('[data-compass-close]');
    if (!trigger || !panel) return;
    const setOpen = open => {
      trigger.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      if (open) window.setTimeout(() => panel.querySelector('a,input,button')?.focus(), 20);
    };
    trigger.addEventListener('click', () => setOpen(panel.hidden));
    close?.addEventListener('click', () => { setOpen(false); trigger.focus(); });
    document.addEventListener('click', e => { if (!panel.hidden && !compass.contains(e.target)) setOpen(false); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !panel.hidden) { setOpen(false); trigger.focus(); } });
  });

  // Subtle hall depth on pointer devices.
  const hall = document.querySelector('[data-hall-stage]');
  const motionStrength = Math.max(0, Math.min(1, parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--bazaar-motion')) || 0));
  if (hall && motionStrength > 0 && !reducedMotion && window.matchMedia('(pointer:fine)').matches) {
    hall.addEventListener('pointermove', e => {
      const rect = hall.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width - .5) * -8 * motionStrength;
      const y = ((e.clientY - rect.top) / rect.height - .5) * -5 * motionStrength;
      hall.style.setProperty('--hall-x', `${x}px`);
      hall.style.setProperty('--hall-y', `${y}px`);
    });
    hall.addEventListener('pointerleave', () => {
      hall.style.setProperty('--hall-x', '0px');
      hall.style.setProperty('--hall-y', '0px');
    });
  }

  // Reveal sections gently as the visitor wanders downward.
  const revealItems = [...document.querySelectorAll('[data-reveal]')];
  if (revealItems.length) {
    if (reducedMotion || !('IntersectionObserver' in window)) revealItems.forEach(el => el.classList.add('is-revealed'));
    else {
      const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: .08 });
      revealItems.forEach(el => observer.observe(el));
    }
  }

  // Product lightbox.
  const lightbox = document.querySelector('[data-product-lightbox]');
  if (lightbox) {
    const image = lightbox.querySelector('[data-lightbox-image]');
    const close = () => { lightbox.hidden = true; document.body.classList.remove('no-scroll'); };
    document.querySelectorAll('[data-lightbox-src]').forEach(btn => btn.addEventListener('click', () => {
      if (image) { image.src = btn.dataset.lightboxSrc; image.alt = btn.getAttribute('aria-label') || ''; }
      lightbox.hidden = false;
      document.body.classList.add('no-scroll');
      lightbox.querySelector('[data-lightbox-close]')?.focus();
    }));
    lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', close);
    lightbox.addEventListener('click', e => { if (e.target === lightbox) close(); });
    document.addEventListener('keydown', e => { if (e.key === 'Escape' && !lightbox.hidden) close(); });
  }

  // Native share with clipboard fallback.
  document.querySelectorAll('[data-share-product]').forEach(btn => btn.addEventListener('click', async () => {
    const data = { title: btn.dataset.shareTitle || document.title, url: btn.dataset.shareUrl || window.location.href };
    try {
      if (navigator.share) await navigator.share(data);
      else if (navigator.clipboard) { await navigator.clipboard.writeText(data.url); toast('Link copied'); }
      else window.location.href = `mailto:?subject=${encodeURIComponent(data.title)}&body=${encodeURIComponent(data.url)}`;
    } catch (_) {}
  }));

  // Local, privacy-friendly recently viewed trail. No account or external tracking required.
  const storageKey = 'hiddenBazaarRecentlyViewedV3';
  const currentJson = document.querySelector('[data-current-product-json]');
  let currentProduct = null;
  if (currentJson) {
    try { currentProduct = JSON.parse(currentJson.textContent || 'null'); } catch (_) {}
  }
  let recent = [];
  try { recent = JSON.parse(localStorage.getItem(storageKey) || '[]'); } catch (_) { recent = []; }
  if (!Array.isArray(recent)) recent = [];

  document.querySelectorAll('[data-recently-viewed-section]').forEach(section => {
    const grid = section.querySelector('[data-recently-viewed-grid]');
    if (!grid) return;
    const visible = recent.filter(item => item && item.url && (!currentProduct || item.url !== currentProduct.url)).slice(0, 4);
    if (!visible.length) return;
    visible.forEach(item => {
      const article = document.createElement('article');
      article.className = 'product-card';
      const link = document.createElement('a');
      link.href = item.url;
      const media = document.createElement('div');
      media.className = 'product-card__media';
      if (item.image) {
        const img = document.createElement('img');
        img.src = item.image;
        img.alt = item.title || '';
        img.loading = 'lazy';
        media.appendChild(img);
      }
      const body = document.createElement('div');
      body.className = 'product-card__body';
      const title = document.createElement('h3');
      title.className = 'product-card__title';
      title.textContent = item.title || 'View find';
      const price = document.createElement('p');
      price.className = 'price';
      price.textContent = item.price || '';
      body.append(title, price);
      link.append(media, body);
      article.append(link);
      grid.appendChild(article);
    });
    section.hidden = false;
  });

  if (currentProduct && currentProduct.url) {
    recent = [currentProduct, ...recent.filter(item => item && item.url !== currentProduct.url)].slice(0, 10);
    try { localStorage.setItem(storageKey, JSON.stringify(recent)); } catch (_) {}
  }


  // V6 immersive home hero: subtle pointer depth without blocking navigation.
  document.querySelectorAll('[data-home-hero]').forEach(hero => {
    if (reducedMotion) return;
    hero.addEventListener('pointermove', event => {
      const rect = hero.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width - .5) * -18;
      const y = ((event.clientY - rect.top) / rect.height - .5) * -12;
      hero.style.setProperty('--hero-x', `${x}px`);
      hero.style.setProperty('--hero-y', `${y}px`);
    }, { passive: true });
    hero.addEventListener('pointerleave', () => {
      hero.style.setProperty('--hero-x', '0px');
      hero.style.setProperty('--hero-y', '0px');
    });
  });

  document.addEventListener('click', event => {
    document.querySelectorAll('.header-v6__nav details[open]').forEach(detail => {
      if (!detail.contains(event.target)) detail.removeAttribute('open');
    });
  });


  // V8 Living Bazaar: progressive search, opt-in sound, daypart and cinematic exits.
  const livingShell = document.querySelector('[data-living-bazaar-shell]');
  if (livingShell) {
    const soundEnabled = livingShell.dataset.soundEnabled === 'true';
    const transitionEnabled = livingShell.dataset.transitionsEnabled === 'true';
    const autoNight = livingShell.dataset.autoNight === 'true';
    const soundPrefKey = 'hiddenBazaarSoundPreferenceV3';
    let soundEngine = null;
    let soundLoadPromise = null;

    const loadSoundEngine = () => {
      if (!soundEnabled) return null;
      if (soundEngine) return soundEngine;
      if (typeof window.createBazaarSound !== 'function') return null;
      soundEngine = window.createBazaarSound({ customUrl: livingShell.dataset.audioUrl || '', volume: Number(livingShell.dataset.audioVolume || .03), cueVolume: .42 });
      return soundEngine;
    };

    const soundToggle = livingShell.querySelector('[data-bazaar-sound-toggle]');
    const soundStatus = livingShell.querySelector('[data-bazaar-sound-status]');
    const soundGate = livingShell.querySelector('[data-bazaar-sound-gate]');
    const setSoundUI = on => {
      if (soundToggle) {
        soundToggle.setAttribute('aria-pressed', String(on));
        soundToggle.setAttribute('aria-label', on ? 'Turn Bazaar ambience off' : 'Turn Bazaar ambience on');
      }
      if (soundStatus) soundStatus.textContent = on ? 'Sound on' : 'Sound off · tap to play';
    };
    const rememberSound = value => { try { localStorage.setItem(soundPrefKey, value); } catch (_) {} };
    const chooseSound = async on => {
      let finalOn = false;
      if (on) {
        const engine = loadSoundEngine();
        finalOn = !!(engine && await engine.enable());
        if (finalOn) engine.playCue('all', .22);
      } else if (soundEngine) soundEngine.disable();
      rememberSound(finalOn ? 'on' : 'off');
      setSoundUI(finalOn);
      if (soundGate) {
        soundGate.dataset.state = 'leaving';
        window.setTimeout(() => soundGate.remove(), reducedMotion ? 0 : 380);
      }
    };

    let storedSound = null;
    try { storedSound = localStorage.getItem(soundPrefKey); } catch (_) {}
    if (storedSound === 'on') {
      // Remember the preference, then resume quietly on the visitor's first fresh interaction.
      setSoundUI(false);
      soundGate?.remove();
      const resumeRememberedSound = async event => {
        if (event?.target?.closest?.('[data-bazaar-sound-toggle]')) return;
        document.removeEventListener('pointerdown', resumeRememberedSound, true);
        document.removeEventListener('keydown', resumeRememberedSound, true);
        const engine = loadSoundEngine();
        const resumed = !!(engine && await engine.enable());
        setSoundUI(resumed);
        if (!resumed) rememberSound('off');
      };
      document.addEventListener('pointerdown', resumeRememberedSound, true);
      document.addEventListener('keydown', resumeRememberedSound, true);
    } else if (storedSound === 'off') {
      setSoundUI(false);
      soundGate?.remove();
    } else {
      setSoundUI(false);
      if (soundGate) window.setTimeout(() => { soundGate.dataset.state = 'ready'; }, 260);
    }

    livingShell.querySelectorAll('[data-bazaar-sound-choice]').forEach(button => {
      button.addEventListener('click', () => chooseSound(button.dataset.bazaarSoundChoice === 'on'));
    });
    soundToggle?.addEventListener('click', async () => {
      const isOn = soundToggle.getAttribute('aria-pressed') === 'true';
      await chooseSound(!isOn);
    });

    // Core Bazaar rooms are controlled by bazaar-rooms.js, independent of Living Mode and navigation.

    // Search overlay. Search remains a normal link if dialog/JS support is missing.
    const searchDialog = livingShell.querySelector('[data-bazaar-search-dialog]');
    document.querySelectorAll('[data-bazaar-search-open]').forEach(link => link.addEventListener('click', event => {
      if (!searchDialog || typeof searchDialog.showModal !== 'function') return;
      event.preventDefault();
      searchDialog.showModal();
      window.setTimeout(() => searchDialog.querySelector('input[type="search"]')?.focus(), 20);
    }));
    livingShell.querySelector('[data-bazaar-search-close]')?.addEventListener('click', () => searchDialog?.close());
    searchDialog?.addEventListener('click', event => { if (event.target === searchDialog) searchDialog.close(); });

    // Subtle time-of-day warmth based on the visitor's own device time.
    if (autoNight) {
      const hour = new Date().getHours();
      document.documentElement.dataset.bazaarTime = (hour >= 19 || hour < 6) ? 'night' : ((hour >= 16 && hour < 19) ? 'dusk' : 'day');
    }


    // Compass audio is intentionally independent from compass visuals.
    // Hover/focus only triggers a short cue: it never changes hotspot geometry,
    // transforms, classes, layout, or pointer hitboxes, so it cannot reintroduce jitter.
    let lastCompassHoverCueAt = 0;
    document.querySelectorAll('[data-bazaar-cue]').forEach(link => {
      let lastActivation = 0;
      let lastHover = 0;

      const canPlay = () => soundToggle && soundToggle.getAttribute('aria-pressed') === 'true';

      const hoverCue = async () => {
        if (!canPlay()) return;
        const now = performance.now();
        // Per-hotspot + global throttles keep fast pointer sweeps musical rather than noisy.
        if (now - lastHover < 420 || now - lastCompassHoverCueAt < 110) return;
        lastHover = now;
        lastCompassHoverCueAt = now;
        const engine = loadSoundEngine();
        engine?.playCue(link.dataset.bazaarCue || 'all', .52);
      };

      const activationCue = async () => {
        if (!canPlay()) return;
        const now = performance.now();
        if (now - lastActivation < 650) return;
        lastActivation = now;
        const engine = loadSoundEngine();
        engine?.playCue(link.dataset.bazaarCue || 'all', .24);
      };

      link.addEventListener('pointerenter', hoverCue, { passive: true });
      link.addEventListener('focus', hoverCue, { passive: true });
      link.addEventListener('pointerdown', activationCue, { passive: true });
      link.addEventListener('keydown', event => {
        if (event.key === 'Enter' || event.key === ' ') activationCue();
      });
    });

    // Cinematic outgoing navigation. Only same-origin GET-style links; modifiers/new tabs remain native.
    const transitionOverlay = livingShell.querySelector('[data-bazaar-page-transition]');
    const transitionLabel = livingShell.querySelector('[data-bazaar-transition-label]');
    if (transitionEnabled && transitionOverlay) {
      document.addEventListener('click', async event => {
        if (window.Shopify && window.Shopify.designMode) return;
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href]'); if (!link || link.target === '_blank' || link.hasAttribute('download')) return;
        if (link.closest('[data-bazaar-search-dialog]')) return;
        let url; try { url = new URL(link.href, window.location.href); } catch (_) { return; }
        if (url.origin !== window.location.origin || ['mailto:','tel:'].includes(url.protocol)) return;
        if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
        if (url.pathname.includes('/checkout')) return;
        const room = link.dataset.bazaarTransition === 'room';
        const duration = reducedMotion ? 40 : (room ? 440 : 230);
        event.preventDefault();
        if (transitionLabel) {
          const name = (link.getAttribute('aria-label') || link.textContent || '').trim().replace(/\s+/g,' ');
          transitionLabel.textContent = room && name ? `Entering ${name}` : 'Following the passage';
        }
        if (soundToggle?.getAttribute('aria-pressed') === 'true') {
          const engine = loadSoundEngine(); engine?.playCue(room ? 'door' : 'all', room ? .25 : .12);
        }
        transitionOverlay.classList.add('is-active');
        window.setTimeout(() => { window.location.assign(url.href); }, duration);
      }, true);
    }
  }

})();
